/**
 * 文件上传管理器 — 核心 Hook
 *
 * 管理文件从"拖入 → 本地预检 → OSS 上传 → 用户编排 → 提交"的完整生命周期。
 *
 * 状态模型:
 *   files: Map<id, FileItem>     — 所有文件（含状态、进度、元数据）
 *   groups: Map<id, FileGroup>   — 文件组（合并后的虚拟容器）
 *   order: string[]              — 顶层排序（文件ID 或 组ID）
 */

import { useState, useCallback, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { validateFile } from "./fileValidation";
import { useOssUploader } from "./useOssUploader";

// =============================================================================
// 类型定义（用 JSDoc 表达）
// =============================================================================

/**
 * @typedef {'CHECKING'|'CHECK_FAILED'|'NEEDS_SPLIT'|'UPLOADING'|'UPLOADED'|'UPLOAD_FAILED'} FilePhase
 *
 * @typedef {Object} FileItem
 * @property {string} id
 * @property {string} name - 文件名
 * @property {File} rawFile - 原始 File 对象
 * @property {FilePhase} phase
 * @property {number} uploadPercent - 0~100
 * @property {string|null} storagePath - OSS 路径
 * @property {string|null} downloadUrl
 * @property {string|null} contentType
 * @property {number|null} fileSize
 * @property {number|null} pageCount - PDF 页数
 * @property {number|null} charCount - Word 字符数
 * @property {Array} issues - 校验问题列表
 * @property {string|null} groupId - 所属组ID（null=独立文件）
 * @property {boolean} isSplit - 是否为拆分产物
 * @property {string|null} splitFromFilename - 拆分来源文件名
 * @property {string|null} splitPages - 拆分的页面范围
 *
 * @typedef {Object} FileGroup
 * @property {string} id
 * @property {string|null} name - 组名
 * @property {string[]} memberIds - 成员文件ID列表
 */

// =============================================================================
// Hook
// =============================================================================

export const buildUploadSubmitPayload = (files, groups) => {
    const groupedFileIds = new Set();
    const fileGroupsPayload = [];

    for (const [, group] of groups) {
        const groupFiles = group.memberIds
            .map((fid) => files.get(fid))
            .filter((file) => file && file.phase === "UPLOADED")
            .map((file) => ({
                object_key: file.storagePath,
                file_name: file.name,
                content_type: file.contentType,
                file_size: file.fileSize,
                is_split: file.isSplit,
                split_from_filename: file.splitFromFilename,
                split_pages: file.splitPages,
            }));

        if (groupFiles.length >= 2) {
            fileGroupsPayload.push({
                group_name: group.name || null,
                files: groupFiles,
            });
            group.memberIds.forEach((fid) => groupedFileIds.add(fid));
        }
    }

    const independentFiles = [];
    for (const [fid, file] of files) {
        if (groupedFileIds.has(fid) || file.phase !== "UPLOADED") continue;
        independentFiles.push({
            object_key: file.storagePath,
            file_name: file.name,
            content_type: file.contentType,
            file_size: file.fileSize,
            is_split: file.isSplit,
            split_from_filename: file.splitFromFilename,
            split_pages: file.splitPages,
        });
    }

    return {
        files: independentFiles,
        file_groups: fileGroupsPayload.length > 0 ? fileGroupsPayload : undefined,
    };
};

const isPdfSubmitFile = (file) => {
    const contentType = String(file?.content_type || "").toLowerCase();
    const filenames = [file?.file_name, file?.object_key].filter(Boolean).map(String);
    return contentType === "application/pdf" || filenames.some((name) => /\.pdf$/i.test(name));
};

export const submitPayloadContainsPdf = (payload) => {
    const independentFiles = Array.isArray(payload?.files) ? payload.files : [];
    const groupedFiles = (payload?.file_groups || []).flatMap((group) => (
        Array.isArray(group?.files) ? group.files : []
    ));
    return [...independentFiles, ...groupedFiles].some(isPdfSubmitFile);
};

export function useFileUploadManager() {
    const [files, setFiles] = useState(new Map());
    const [groups, setGroups] = useState(new Map());
    const [order, setOrder] = useState([]); // 顶层排序: fileId 或 groupId
    const { upload } = useOssUploader();

    // 用于防止并发修改
    const filesRef = useRef(files);
    filesRef.current = files;

    // =========================================================================
    // 内部工具
    // =========================================================================

    const updateFile = useCallback((id, patch) => {
        setFiles((prev) => {
            const next = new Map(prev);
            const file = next.get(id);
            if (file) {
                next.set(id, { ...file, ...patch });
            }
            return next;
        });
    }, []);

    // =========================================================================
    // 阶段 1 + 2: 添加文件（预检 → 上传）
    // =========================================================================

    const addFiles = useCallback(async (rawFiles) => {
        const fileList = Array.from(rawFiles);
        if (fileList.length === 0) return;

        // 获取当前已有文件名
        const existingNames = Array.from(filesRef.current.values()).map((f) => f.name);

        // 为每个文件创建 FileItem 并加入状态
        const newItems = [];
        for (const rawFile of fileList) {
            const id = uuidv4();
            const item = {
                id,
                name: rawFile.name,
                rawFile,
                phase: "CHECKING",
                uploadPercent: 0,
                storagePath: null,
                downloadUrl: null,
                contentType: rawFile.type,
                fileSize: rawFile.size,
                pageCount: null,
                charCount: null,
                issues: [],
                groupId: null,
                isSplit: false,
                splitFromFilename: null,
                splitPages: null,
            };
            newItems.push(item);
            existingNames.push(rawFile.name);
        }

        // 批量更新状态
        setFiles((prev) => {
            const next = new Map(prev);
            newItems.forEach((item) => next.set(item.id, item));
            return next;
        });
        setOrder((prev) => [...prev, ...newItems.map((item) => item.id)]);

        // 逐个执行阶段 1 校验 + 阶段 2 上传
        for (const item of newItems) {
            // 阶段 1: 校验
            const result = await validateFile(item.rawFile, existingNames.filter((n) => n !== item.name));

            if (result.phase === "blocked") {
                updateFile(item.id, {
                    phase: "CHECK_FAILED",
                    issues: result.issues,
                    pageCount: result.metadata.pageCount || null,
                    charCount: result.metadata.charCount || null,
                });
                continue;
            }

            const patchAfterCheck = {
                issues: result.issues,
                pageCount: result.metadata.pageCount || null,
                charCount: result.metadata.charCount || null,
            };

            if (result.phase === "needs_split") {
                updateFile(item.id, { ...patchAfterCheck, phase: "NEEDS_SPLIT" });
                // 需要拆分的文件也上传（拆分后原文件保留但不参与 IDS 创建）
            } else {
                updateFile(item.id, { ...patchAfterCheck, phase: "UPLOADING" });
            }

            // 阶段 2: OSS 上传
            try {
                const phase = result.phase === "needs_split" ? "NEEDS_SPLIT" : "UPLOADING";
                if (phase !== "NEEDS_SPLIT") {
                    updateFile(item.id, { phase: "UPLOADING" });
                }

                const uploadResult = await upload(item.rawFile, (percent) => {
                    updateFile(item.id, { uploadPercent: Math.round(percent) });
                });

                updateFile(item.id, {
                    phase: result.phase === "needs_split" ? "NEEDS_SPLIT" : "UPLOADED",
                    uploadPercent: 100,
                    storagePath: uploadResult.storage_path,
                    downloadUrl: uploadResult.download_url,
                    contentType: uploadResult.content_type,
                    fileSize: uploadResult.file_size,
                });
            } catch (err) {
                console.error(`[FileUploadManager] 上传失败 (${item.name}):`, err);
                updateFile(item.id, {
                    phase: "UPLOAD_FAILED",
                    issues: [
                        ...(result.issues || []),
                        { code: "UPLOAD_ERROR", message: err.message, severity: "error" },
                    ],
                });
            }
        }
    }, [upload, updateFile]);

    // =========================================================================
    // 添加拆分文件（拆分弹窗完成后调用）
    // =========================================================================

    const addSplitFiles = useCallback(async (originalFileId, subFiles) => {
        const originalFile = filesRef.current.get(originalFileId);
        if (!originalFile) return;

        const newItems = [];
        for (const sub of subFiles) {
            const id = uuidv4();
            newItems.push({
                id,
                name: sub.filename,
                rawFile: sub.file,
                phase: "UPLOADING",
                uploadPercent: 0,
                storagePath: null,
                downloadUrl: null,
                contentType: sub.file.type,
                fileSize: sub.file.size,
                pageCount: null,
                charCount: null,
                issues: [],
                groupId: null,
                isSplit: true,
                splitFromFilename: originalFile.name,
                splitPages: sub.pageRange,
            });
        }

        // 加入状态
        setFiles((prev) => {
            const next = new Map(prev);
            newItems.forEach((item) => next.set(item.id, item));
            return next;
        });

        // 插入到原文件之后
        setOrder((prev) => {
            const idx = prev.indexOf(originalFileId);
            const insertAt = idx >= 0 ? idx + 1 : prev.length;
            const newOrder = [...prev];
            newItems.forEach((item, i) => {
                newOrder.splice(insertAt + i, 0, item.id);
            });
            return newOrder;
        });

        // 逐个上传
        for (const item of newItems) {
            try {
                const uploadResult = await upload(item.rawFile, (percent) => {
                    updateFile(item.id, { uploadPercent: Math.round(percent) });
                });
                updateFile(item.id, {
                    phase: "UPLOADED",
                    uploadPercent: 100,
                    storagePath: uploadResult.storage_path,
                    downloadUrl: uploadResult.download_url,
                });
            } catch (err) {
                updateFile(item.id, {
                    phase: "UPLOAD_FAILED",
                    issues: [{ code: "UPLOAD_ERROR", message: err.message, severity: "error" }],
                });
            }
        }
    }, [upload, updateFile]);

    // =========================================================================
    // 阶段 3: 用户编排操作
    // =========================================================================

    /** 移除文件 */
    const removeFile = useCallback((fileId) => {
        setFiles((prev) => {
            const next = new Map(prev);
            next.delete(fileId);
            return next;
        });
        // 从组中移除
        setGroups((prev) => {
            const next = new Map(prev);
            for (const [gid, group] of next) {
                const newMembers = group.memberIds.filter((id) => id !== fileId);
                if (newMembers.length < 2) {
                    // 少于 2 个成员，自动解散
                    newMembers.forEach((mid) => {
                        setFiles((fp) => {
                            const fn = new Map(fp);
                            const f = fn.get(mid);
                            if (f) fn.set(mid, { ...f, groupId: null });
                            return fn;
                        });
                    });
                    // 把剩余的成员放回顶层排序
                    setOrder((o) => {
                        const updated = o.filter((id) => id !== gid);
                        const gidIdx = o.indexOf(gid);
                        const insertAt = gidIdx >= 0 ? gidIdx : updated.length;
                        newMembers.forEach((mid, i) => {
                            if (!updated.includes(mid)) {
                                updated.splice(insertAt + i, 0, mid);
                            }
                        });
                        return updated;
                    });
                    next.delete(gid);
                } else {
                    next.set(gid, { ...group, memberIds: newMembers });
                }
            }
            return next;
        });
        setOrder((prev) => prev.filter((id) => id !== fileId));
    }, []);

    /** 创建文件组（勾选多个文件 → 合并） */
    const createGroup = useCallback((fileIds, groupName = null) => {
        if (fileIds.length < 2) return;

        const gid = uuidv4();
        const group = { id: gid, name: groupName, memberIds: [...fileIds] };

        setGroups((prev) => new Map(prev).set(gid, group));

        // 标记文件
        setFiles((prev) => {
            const next = new Map(prev);
            fileIds.forEach((fid) => {
                const f = next.get(fid);
                if (f) next.set(fid, { ...f, groupId: gid });
            });
            return next;
        });

        // 更新排序：移除文件ID，插入组ID
        setOrder((prev) => {
            const firstIdx = Math.min(...fileIds.map((id) => prev.indexOf(id)).filter((i) => i >= 0));
            const filtered = prev.filter((id) => !fileIds.includes(id));
            filtered.splice(firstIdx >= 0 ? Math.min(firstIdx, filtered.length) : filtered.length, 0, gid);
            return filtered;
        });
    }, []);

    /** 拖拽合并（sourceId 拖到 targetId 上） */
    const mergeFiles = useCallback((sourceId, targetId) => {
        const sourceFile = filesRef.current.get(sourceId);
        const targetFile = filesRef.current.get(targetId);
        if (!sourceFile || !targetFile) return;

        // 如果目标已在组中，加入该组
        if (targetFile.groupId) {
            addToGroup(sourceId, targetFile.groupId);
            return;
        }

        // 用第一个文件名（去扩展名）+ "(合并)" 作为默认组名
        const baseName = targetFile.name.replace(/\.[^.]+$/, "");
        const defaultGroupName = `${baseName} (合并)`;
        createGroup([targetId, sourceId], defaultGroupName);
    }, [createGroup]);

    /** 加入已有组 */
    const addToGroup = useCallback((fileId, groupId) => {
        setGroups((prev) => {
            const next = new Map(prev);
            const group = next.get(groupId);
            if (group && !group.memberIds.includes(fileId)) {
                next.set(groupId, { ...group, memberIds: [...group.memberIds, fileId] });
            }
            return next;
        });
        setFiles((prev) => {
            const next = new Map(prev);
            const f = next.get(fileId);
            if (f) next.set(fileId, { ...f, groupId });
            return next;
        });
        setOrder((prev) => prev.filter((id) => id !== fileId));
    }, []);

    /** 从组中移出 */
    const removeFromGroup = useCallback((fileId, groupId) => {
        setGroups((prev) => {
            const next = new Map(prev);
            const group = next.get(groupId);
            if (!group) return next;

            const newMembers = group.memberIds.filter((id) => id !== fileId);
            if (newMembers.length < 2) {
                // 解散组
                newMembers.forEach((mid) => {
                    setFiles((fp) => {
                        const fn = new Map(fp);
                        const f = fn.get(mid);
                        if (f) fn.set(mid, { ...f, groupId: null });
                        return fn;
                    });
                });
                const gidIdx = order.indexOf(groupId);
                setOrder((o) => {
                    const updated = o.filter((id) => id !== groupId);
                    const insertAt = gidIdx >= 0 ? gidIdx : updated.length;
                    newMembers.forEach((mid, i) => {
                        if (!updated.includes(mid)) updated.splice(insertAt + i, 0, mid);
                    });
                    if (!updated.includes(fileId)) updated.splice(insertAt + newMembers.length, 0, fileId);
                    return updated;
                });
                next.delete(groupId);
            } else {
                next.set(groupId, { ...group, memberIds: newMembers });
            }
            return next;
        });

        // 标记文件为独立
        setFiles((prev) => {
            const next = new Map(prev);
            const f = next.get(fileId);
            if (f) next.set(fileId, { ...f, groupId: null });
            return next;
        });

        // 文件加入顶层排序（组后面）
        setOrder((prev) => {
            if (prev.includes(fileId)) return prev;
            const gidIdx = prev.indexOf(groupId);
            const insertAt = gidIdx >= 0 ? gidIdx + 1 : prev.length;
            const next = [...prev];
            next.splice(insertAt, 0, fileId);
            return next;
        });
    }, [order]);

    /** 解散组 */
    const disbandGroup = useCallback((groupId) => {
        const group = groups.get(groupId);
        if (!group) return;

        // 所有成员标记为独立
        setFiles((prev) => {
            const next = new Map(prev);
            group.memberIds.forEach((fid) => {
                const f = next.get(fid);
                if (f) next.set(fid, { ...f, groupId: null });
            });
            return next;
        });

        // 替换排序中的组ID为成员ID
        setOrder((prev) => {
            const idx = prev.indexOf(groupId);
            const next = prev.filter((id) => id !== groupId);
            const insertAt = idx >= 0 ? idx : next.length;
            group.memberIds.forEach((fid, i) => {
                if (!next.includes(fid)) next.splice(insertAt + i, 0, fid);
            });
            return next;
        });

        setGroups((prev) => {
            const next = new Map(prev);
            next.delete(groupId);
            return next;
        });
    }, [groups]);

    /** 修改组名（最大 200 字符，与后端字段限制一致） */
    const renameGroup = useCallback((groupId, newName) => {
        const trimmed = newName ? newName.slice(0, 200) : newName;
        setGroups((prev) => {
            const next = new Map(prev);
            const group = next.get(groupId);
            if (group) next.set(groupId, { ...group, name: trimmed });
            return next;
        });
    }, []);

    /** 重排序 */
    const reorderItems = useCallback((activeId, overId) => {
        setOrder((prev) => {
            const oldIdx = prev.indexOf(activeId);
            const newIdx = prev.indexOf(overId);
            if (oldIdx < 0 || newIdx < 0) return prev;
            const next = [...prev];
            next.splice(oldIdx, 1);
            next.splice(newIdx, 0, activeId);
            return next;
        });
    }, []);

    // =========================================================================
    // 阶段 4: 构建提交数据
    // =========================================================================

    const submitPayload = useMemo(() => buildUploadSubmitPayload(files, groups), [files, groups]);
    const hasSubmittablePdf = useMemo(() => submitPayloadContainsPdf(submitPayload), [submitPayload]);
    const getSubmitPayload = useCallback(() => submitPayload, [submitPayload]);

    /** 提交就绪检查 */
    const canSubmit = submitPayload.files.length > 0 || Boolean(submitPayload.file_groups?.length);
    const hasUploading = Array.from(files.values()).some((f) => f.phase === "UPLOADING" || f.phase === "CHECKING");

    /** 重置所有状态 */
    const reset = useCallback(() => {
        setFiles(new Map());
        setGroups(new Map());
        setOrder([]);
    }, []);

    // =========================================================================
    // 导出
    // =========================================================================

    return {
        // 状态
        files,
        groups,
        order,
        canSubmit,
        hasUploading,
        submitPayload,
        hasSubmittablePdf,

        // 阶段 1+2
        addFiles,
        addSplitFiles,

        // 阶段 3: 编排
        removeFile,
        createGroup,
        mergeFiles,
        addToGroup,
        removeFromGroup,
        disbandGroup,
        renameGroup,
        reorderItems,

        // 阶段 4
        getSubmitPayload,
        reset,
    };
}
